import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../global.css';
import Navbar from '../../components/Navbar';
import SiteFooter from '../../components/SiteFooter';
import NeuralCanvas from '../../components/NeuralCanvas';
import { useSEO } from '../../hooks/useSEO';

const TOC = [
  ['abstract', 'Abstract'],
  ['opportunity', '1 · The Opportunity'],
  ['challenges', '2 · Challenges'],
  ['architecture', '3 · Architecture'],
  ['capabilities', '4 · Capabilities'],
  ['safety', '5 · Safety'],
  ['impact', '6 · Impact'],
  ['roadmap', '7 · Roadmap'],
  ['conclusion', '8 · Conclusion'],
  ['references', 'References'],
];

function Section({ id, num, title, children }) {
  return (
    <section id={id} style={{ marginBottom: 56, scrollMarginTop: 80 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>{num}</div>
      <h2 style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-0.02em', marginBottom: 16, color: 'var(--fg)', lineHeight: 1.2 }}>{title}</h2>
      <div style={{ fontSize: 14, lineHeight: 1.82, color: 'var(--fg-mid)' }}>{children}</div>
    </section>
  );
}

function P({ children }) { return <p style={{ marginBottom: 14 }}>{children}</p>; }

function Ul({ items }) {
  return (
    <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '12px 0' }}>
      {items.map(item => (
        <li key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--fg-dim)', marginTop: 8, flexShrink: 0 }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function InfoBox({ children }) {
  return (
    <div style={{ padding: '16px 20px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', margin: '20px 0', fontSize: 14, lineHeight: 1.75, color: 'var(--fg-mid)' }}>
      {children}
    </div>
  );
}

export default function Whitepaper() {
  useSEO({
    title: 'Research Whitepaper',
    description: 'The Cybernate technical whitepaper: architecture, capabilities, safety design, and roadmap for Africa\'s general intelligence platform.',
    path: '/whitepaper',
  });
  const [active, setActive] = useState('abstract');

  useEffect(() => {
    const handler = () => {
      const sections = TOC.map(([id]) => document.getElementById(id)).filter(Boolean);
      const inView = sections.find(s => {
        const r = s.getBoundingClientRect();
        return r.top >= 0 && r.top < 220;
      });
      if (inView) setActive(inView.id);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <>
      <NeuralCanvas />
      <div className="neural-page">
      <Navbar />

      <div style={{ borderBottom: '1px solid var(--line)', padding: '64px 0 48px', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>
            Whitepaper · 2026 · v1.0
          </div>
          <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 12, maxWidth: 640 }}>
            General-Purpose AI for Africa
          </h1>
          <p style={{ fontSize: 14, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', letterSpacing: '0.06em', marginBottom: 28 }}>
            Published May 2026 · Cybernate AI Research · DOI: CY/2026/001
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => window.print()}>
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M3 5V2H11V5M3 10H2C1.4 10 1 9.6 1 9V6C1 5.4 1.4 5 2 5H12C12.6 5 13 5.4 13 6V9C13 9.6 12.6 10 12 10H11M3 8H11V13H3V8Z" stroke="currentColor" strokeWidth="1.3"/></svg>
              Download PDF
            </button>
            <Link to="/signup" className="btn btn-ghost">Get started →</Link>
          </div>
        </div>
      </div>

      <div className="wp-layout">

        <aside style={{ position: 'sticky', top: 80, paddingTop: 48 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 14 }}>Contents</div>
          {TOC.map(([id, label]) => (
            <button key={id} onClick={() => { setActive(id); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: 'var(--r-sm)', background: active === id ? 'var(--bg-2)' : 'transparent', border: 'none', fontSize: 13, color: active === id ? 'var(--fg)' : 'var(--fg-dim)', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 2 }}>
              {label}
            </button>
          ))}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--line)' }}>
            <Link to="/signup" style={{ fontSize: 13, color: 'var(--accent)', display: 'block' }}>Get started →</Link>
          </div>
        </aside>

        <div style={{ paddingTop: 56, paddingBottom: 80 }}>

          {/* Key stats */}
          <div className="wp-stats-grid">
            {[['54+', 'Languages'], ['<1s', 'Response time'], ['98.2%', 'Accuracy'], ['5', 'Modalities']].map(([n, l]) => (
              <div key={n} style={{ padding: '20px 16px', background: 'var(--bg)', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: 24, letterSpacing: '-0.04em', color: 'var(--fg)', marginBottom: 6 }}>{n}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{l}</div>
              </div>
            ))}
          </div>

          <Section id="abstract" num="00 :: Abstract" title="Abstract">
            <InfoBox>
              Africa represents a unique and underserved frontier for artificial intelligence. With 1.4 billion people, 54 nations, 2,000+ languages, and the world's fastest-growing digital economy, the continent requires AI infrastructure built for its specific context — not retrofitted from systems designed elsewhere. This paper presents Cybernate, a general-purpose AI platform engineered for Africa's infrastructure realities, language diversity, and sectoral priorities.
            </InfoBox>
          </Section>

          <Section id="opportunity" num="01 :: Opportunity" title="The African AI Opportunity">
            <P>Sub-Saharan Africa is experiencing the most rapid digital transformation in human history. With 600 million mobile internet users projected by 2026, smartphone penetration growing at 9% annually, and a median population age of 19.7 years, the continent is uniquely positioned to leapfrog traditional technology infrastructure.</P>
            <P>The McKinsey Global Institute estimates that AI could contribute $180 billion to Africa's GDP by 2030. Yet, as of 2025, less than 2% of global AI research is focused on African contexts, and fewer than 5% of commercially available language models support African languages beyond basic translation.</P>
            <div style={{ padding: '16px 20px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', marginTop: 20 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>AI Investment Share (2025)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[['Africa', 2, 'var(--signal)'], ['North America', 54, 'var(--accent)'], ['Europe', 30, 'var(--fg-dim)']].map(([label, pct, color]) => (
                  <div key={label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--fg-mid)', marginBottom: 6 }}>
                      <span>{label}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', color }}>{pct}%</span>
                    </div>
                    <div style={{ height: 5, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          <Section id="challenges" num="02 :: Challenges" title="Current Challenges">
            <P>Building AI infrastructure for Africa requires solving four distinct categories of challenges:</P>
            <Ul items={[
              'Language diversity: Africa\'s 2,000+ languages are largely absent from global AI training datasets.',
              'Infrastructure constraints: Average bandwidth of 8.4 Mbps vs 200+ Mbps in developed markets — AI systems optimised for high-bandwidth fail here.',
              'Data sovereignty: 24 African nations require data localisation; compliance needs physical presence in-region.',
              'Contextual alignment: Models trained on Western data inherit Western biases and can\'t model local behaviour (e.g. mobile money vs. credit scores).',
            ]} />
          </Section>

          <Section id="architecture" num="03 :: Architecture" title="Platform Architecture">
            <P>Cybernate's architecture is designed around modularity, efficient inference, and geographic distribution across five layers:</P>
            <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-md)', overflow: 'hidden', marginTop: 16 }}>
              {[
                { layer: 'API Gateway', desc: 'Request routing, authentication, and rate management', tech: 'High-speed API runtime' },
                { layer: 'Inference Cluster', desc: 'GPU-accelerated processing with automatic load balancing', tech: 'Optimised AI runtime' },
                { layer: 'Language Engine', desc: 'Fine-tuned models with African language adapters', tech: 'Multilingual AI models' },
                { layer: 'Vision Engine', desc: 'Object detection and scene understanding, trained on African environments', tech: 'Real-time visual AI' },
                { layer: 'Data Layer', desc: 'Distributed storage across African regional nodes', tech: 'Distributed databases' },
              ].map((l, i) => (
                <div key={l.layer} className="wp-arch-row" style={{ background: i % 2 === 0 ? 'var(--bg-1)' : 'var(--bg)' }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg)' }}>{l.layer}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-dim)' }}>{l.desc}</div>
                  <div className="wp-arch-tech" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', whiteSpace: 'nowrap', letterSpacing: '0.06em' }}>{l.tech}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section id="capabilities" num="04 :: Capabilities" title="Core Capabilities">
            <div className="wp-caps-grid">
              {[
                ['Language', 'Chat, summarisation, translation, Q&A across 18+ African languages with cultural context.'],
                ['Vision', 'Object detection and scene understanding, fine-tuned on African environments.'],
                ['Embeddings', 'Semantic search with multilingual vectors optimised for African text.'],
                ['Reasoning', 'Multi-step planning for complex tasks — agri advisory, safety triage, document analysis.'],
                ['Memory', 'Persistent context across sessions for long-running, stateful applications.'],
              ].map(([title, desc]) => (
                <div key={title} style={{ padding: '18px 20px', background: 'var(--bg)' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>{title}</div>
                  <div style={{ fontSize: 13, color: 'var(--fg-mid)', lineHeight: 1.65 }}>{desc}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section id="safety" num="05 :: Safety" title="Safety & Governance">
            <P>Safety is designed into every layer of the platform. We operate under a tiered framework aligned with NDPR (Nigeria), POPIA (South Africa), and Kenya's Data Protection Act.</P>
            <P>All inference runs in-region. No African user data is processed outside African infrastructure. Model outputs are filtered through a context-aware safety classifier trained on African social norms and legal standards.</P>
          </Section>

          <Section id="impact" num="06 :: Impact" title="Measured Impact">
            <div className="wp-impact-grid">
              {[['34%', 'Reduction in diagnostic error vs Western-only models'], ['2.4M', 'Safety signals processed monthly'], ['18', 'African languages in production']].map(([n, l]) => (
                <div key={n} style={{ padding: '20px', background: 'var(--bg)' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 500, letterSpacing: '-0.04em', color: 'var(--fg)', marginBottom: 8 }}>{n}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-dim)', lineHeight: 1.6 }}>{l}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section id="roadmap" num="07 :: Roadmap" title="Roadmap">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingLeft: 8 }}>
              {[
                { q: 'Q2 2025', t: 'Public API Launch', items: ['Language API (18 languages)', 'Vision API', 'Node.js + Python SDKs'], done: true },
                { q: 'Q3 2025', t: 'Expanded Capabilities', items: ['Embeddings API v2', 'Reasoning Agents', 'Lagos + Nairobi clusters'], done: true },
                { q: 'Q4 2025', t: 'Scale & Compliance', items: ['NDPR / POPIA compliance', 'On-premise kit', '36 language support'], done: false },
                { q: 'Q1 2026', t: 'AI Marketplace', items: ['Pre-built sector models', 'Fine-tuning API'], done: false },
                { q: 'Q3 2026', t: 'West & North Africa', items: ['Accra cluster (ACC-01)', 'Cairo cluster (CAI-01)'], done: false },
              ].map((r) => (
                <div key={r.q} style={{ display: 'flex', gap: 20, paddingBottom: 28, borderLeft: `1px solid ${r.done ? 'var(--signal)' : 'var(--line)'}`, paddingLeft: 24, position: 'relative' }}>
                  <div style={{ position: 'absolute', left: -5, top: 2, width: 10, height: 10, borderRadius: '50%', background: r.done ? 'var(--signal)' : 'var(--bg-3)', border: '2px solid var(--line-2)' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: r.done ? 'var(--signal)' : 'var(--fg-dim)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>{r.q}{r.done ? ' · COMPLETE' : ''}</div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg)', marginBottom: 8 }}>{r.t}</div>
                    <Ul items={r.items} />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section id="conclusion" num="08 :: Conclusion" title="Conclusion">
            <P>Africa is not a homogenous market waiting for Western AI to arrive — it is a collection of distinct, sophisticated economies with specific needs, languages, and regulatory requirements. Cybernate is built from first principles for this reality.</P>
            <P>The infrastructure advantage matters. Models served from Lagos run faster in Lagos. Data that stays in Africa complies with African law. Language models trained on African text understand African context.</P>
            <InfoBox>
              The question is not whether Africa will have world-class AI infrastructure. The question is whether that infrastructure will be built by Africans, for Africans — or imported at a premium and retrofitted.
            </InfoBox>
          </Section>

          <Section id="references" num="REF :: References" title="References">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                '[1] GSMA Mobile Economy Sub-Saharan Africa 2025.',
                '[2] ITU Digital Development Report 2024.',
                '[3] UN World Population Prospects 2024.',
                '[4] McKinsey Global Institute: Africa\'s AI Opportunity, 2023.',
                '[5] Adelani et al. "The State of African Language NLP." ACL 2024.',
                '[6] Obermeyer et al. "Dissecting racial bias in AI health risk algorithms." Science, 2019.',
                '[7] Buolamwini & Gebru. "Gender Shades." FAccT, 2018.',
              ].map(ref => (
                <div key={ref} style={{ fontSize: 13, color: 'var(--fg-dim)', lineHeight: 1.65, padding: '9px 14px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}>
                  {ref}
                </div>
              ))}
            </div>
          </Section>

        </div>
      </div>

      <SiteFooter />
      </div>
    </>
  );
}
